import { useTranslation } from '@/i18n'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Switch } from '@/components/ui/switch'
import { IntuiBadge } from '@/components/intueri/IntuiBadge/IntuiBadge'
import { timeAgo } from '@/utils/timeUtils'
import type { Workflow } from './types'
import ExecutorAvatars from './ExecutorAvatars'
import WorkflowRowMenu, { type WorkflowRowActions } from './WorkflowRowMenu'

/**
 * List/table view (Figma "1. Workflow Entry Point" — table): Name · Description
 * · Executors · Last activated · Active (toggle), with the kebab on row hover.
 * Draft rows carry the Draft pill next to the name. (No "Active Every" column —
 * Figma's list view doesn't have it.)
 */
export default function WorkflowTable({
  workflows,
  canManage = true,
  menuHidden = false,
  isActive,
  onToggleActive,
  ...actions
}: {
  workflows: Workflow[]
  canManage?: boolean
  /** Hide the row menu entirely (annotators — read-only everywhere). */
  menuHidden?: boolean
  /** The schedule toggle state per workflow (client-side settings for now). */
  isActive?: (workflow: Workflow) => boolean
  onToggleActive?: (workflow: Workflow, active: boolean) => void
} & WorkflowRowActions) {
  const { t } = useTranslation()
  const { onOpen } = actions
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[240px]">{t('workflow:table.name', { defaultValue: 'Name' })}</TableHead>
          <TableHead>{t('workflow:table.description', { defaultValue: 'Description' })}</TableHead>
          <TableHead className="w-[110px]">{t('workflow:table.executors', { defaultValue: 'Executors' })}</TableHead>
          <TableHead className="w-[140px] whitespace-nowrap">{t('workflow:table.created_by', { defaultValue: 'Created by' })}</TableHead>
          <TableHead className="w-[160px] whitespace-nowrap">{t('workflow:table.last_activated', { defaultValue: 'Last activated' })}</TableHead>
          <TableHead className="w-[110px]">{t('workflow:table.schedule', { defaultValue: 'Schedule' })}</TableHead>
          {/* The row menu gets its own column (UI-44, Putra 2026-09-16): sharing
              the Schedule cell put an unlabelled icon under the word "Schedule",
              reading as part of the toggle. The header is sr-only because the
              column shows an icon, not a title — screen readers still get a name
              for the column, sighted users see the icon speak for itself. */}
          <TableHead className="w-[56px]">
            <span className="sr-only">{t('workflow:row.more', { defaultValue: 'More actions' })}</span>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {workflows.map((w) => (
          <TableRow
            key={w.id}
            className="group cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            role="button"
            tabIndex={0}
            aria-label={w.name}
            onClick={() => onOpen?.(w)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen?.(w) }
            }}
          >
            {/* max-w caps the cell so a long name TRUNCATES instead of stretching
                the column and breaking the row layout (table cells auto-expand
                to content otherwise). min-w-0 lets the flex child shrink. */}
            <TableCell className="max-w-[240px] font-medium text-foreground">
              <span className="flex min-w-0 items-center gap-2">
                <span className="min-w-0 truncate" title={w.name}>{w.name}</span>
                {w.draft && (
                  <IntuiBadge variant="secondary" className="shrink-0">
                    {t('workflow:row.draft', { defaultValue: 'Draft' })}
                  </IntuiBadge>
                )}
              </span>
            </TableCell>
            {/* max-w-0 lets the auto table layout give this column the leftover
                width; the inner block span is what actually truncates. */}
            <TableCell className="max-w-0 text-muted-foreground">
              <span className="block min-w-0 truncate" title={w.description}>{w.description}</span>
            </TableCell>
            <TableCell><ExecutorAvatars executors={w.executors} size="sm" /></TableCell>
            {/* Attribution: the list is TENANT-shared, so without it a growing list
                gives no way to tell whose workflow is whose (FS item 7). Built-ins
                have no author and read as shared templates. */}
            <TableCell className="max-w-[140px] truncate whitespace-nowrap text-muted-foreground">
              {w.builtin
                ? t('workflow:table.builtin_author', { defaultValue: 'Built-in' })
                : (w.createdByName ?? t('workflow:table.unknown_author', { defaultValue: '—' }))}
            </TableCell>
            {/* whitespace-nowrap: the full date ("Jul 13, 2026 · 23:35") must
                stay on ONE line (Putra 2026-07-15). */}
            <TableCell className="whitespace-nowrap text-muted-foreground">
              {w.lastOpenedAt
                ? timeAgo(w.lastOpenedAt)
                : t('workflow:table.never_opened', { defaultValue: 'Never' })}
            </TableCell>
            {/* Both cells stop propagation: the row itself opens the workflow,
                and neither the toggle nor the menu should trigger that. */}
            <TableCell onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={isActive ? isActive(w) : false}
                disabled={!canManage}
                onCheckedChange={(checked) => onToggleActive?.(w, checked)}
                aria-label={t('workflow:row.schedule', { defaultValue: 'Schedule' })}
              />
            </TableCell>
            <TableCell className="w-[56px]" onClick={(e) => e.stopPropagation()}>
              <span className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                <WorkflowRowMenu workflow={w} canManage={canManage} hidden={menuHidden} {...actions} />
              </span>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
